"use client";

import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
const sections = [{
  id: "hero",
  label: "Home"
}, {
  id: "saas-features",
  label: "Features"
}, {
  id: "why-choose",
  label: "Why Choose"
}, {
  id: "reviews",
  label: "Reviews"
}, {
  id: "blog",
  label: "Blog"
}, {
  id: "contact",
  label: "Contact"
}, {
  id: "footer",
  label: "Footer"
}];
export default function Hero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineLine1 = "Digital Sovereignty";
  const headlineLine2 = "Commanded.";
  return <section data-section-id="4469" id="hero" className="relative min-h-screen w-full overflow-hidden bg-background text-foreground dark">
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1729652/img/cyber_security_globe_network.png" alt="Abstract glowing digital globe representing global network security" className="h-full w-full object-cover animate-[slowZoom_20s_ease-out_infinite_alternate]" loading="eager" decoding="async" />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to top, var(--background) 0%, transparent 50%, transparent 100%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "linear-gradient(to right, hsl(221, 40%, 4% / 0.7) 0%, hsl(221, 40%, 4% / 0.3) 50%, hsl(221, 40%, 4% / 0.5) 100%)"
      }} />
        <div className="absolute inset-0" style={{
        background: "radial-gradient(ellipse at center, transparent 0%, hsl(221, 40%, 4% / 0.4) 100%)"
      }} />
      </div>

      <div className="absolute top-8 right-6 z-20 hidden md:flex items-center gap-6">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="text-primary hover:text-primary/80 font-mono text-xs uppercase tracking-[0.25em]">
              Menu <ChevronDown className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-background border-border">
            {sections.map(section => <DropdownMenuItem key={section.id} asChild>
                <Link to={`#${section.id}`} className="w-full cursor-pointer">
                  {section.label}
                </Link>
              </DropdownMenuItem>)}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary" style={{
        writingMode: "vertical-rl"
      }}>
          Status: Active Protection
        </div>
      </div>

      <div className="absolute top-0 left-0 right-0 h-px bg-border/40 z-10" />
      <div className="absolute top-24 left-6 right-6 md:left-12 md:right-12 h-px bg-border/20 z-10 hidden md:block" />

      <div ref={ref} className="relative z-10 flex min-h-screen flex-col justify-end px-6 pb-20 pt-32 md:px-12 md:pb-24 lg:px-16 lg:pb-28">
        <div className="max-w-7xl">
          <div className={`flex items-center gap-4 mb-8 md:mb-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="h-px w-12 bg-primary" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.3em] text-primary">
              VPNGENIX — Command Center for Digital Sovereignty
            </span>
          </div>

          <h1 className="font-default font-bold leading-[0.95] text-foreground mb-8 md:mb-10" style={{
          fontSize: "clamp(3rem, 8vw, 7.5rem)"
        }}>
            <span className="block overflow-hidden">
              {headlineLine1.split("").map((char, i) => <span key={`l1-${i}`} data-index={i} className="inline-block transition-all ease-out" style={{
              transitionDuration: "1200ms",
              transitionDelay: `${300 + i * 40}ms`,
              opacity: isVisible ? 1 : 0,
              filter: isVisible ? "blur(0px)" : "blur(8px)",
              transform: isVisible ? "translateY(0)" : "translateY(20px)"
            }}>
                  {char === " " ? "\u00A0" : char}
                </span>)}
            </span>
            <span className="block italic text-primary overflow-hidden">
              {headlineLine2.split("").map((char, i) => <span key={`l2-${i}`} data-index={i} className="inline-block transition-all ease-out" style={{
              transitionDuration: "1200ms",
              transitionDelay: `${900 + i * 40}ms`,
              opacity: isVisible ? 1 : 0,
              filter: isVisible ? "blur(0px)" : "blur(8px)",
              transform: isVisible ? "translateY(0)" : "translateY(20px)"
            }}>
                  {char === " " ? "\u00A0" : char}
                </span>)}
            </span>
          </h1>

          <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed mb-10 md:mb-12 max-w-[480px] transition-all duration-1000 ease-out" style={{
          transitionDelay: "1600ms",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            Military-grade encryption meets consumer-friendly accessibility. Secure your digital footprint with the most trusted VPN infrastructure on the planet.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 sm:items-center transition-all duration-1000 ease-out" style={{
          transitionDelay: "1800ms",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-8 font-mono text-xs uppercase tracking-[0.25em] shadow-lg">
              <Link to="https://vpngenix.com/go/NordVPN" newTab>Get Protected Now</Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="text-foreground hover:bg-foreground/5 hover:text-primary rounded-sm h-12 px-4 font-mono text-xs uppercase tracking-[0.25em] group">
              <Link to="/CybersecurityCyberGenixPage">
                <span>Learn Our Protocol</span>
                <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-6 md:right-12 z-20 hidden md:flex flex-col items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-primary/80" style={{
        writingMode: "vertical-rl"
      }}>
          Scroll
        </span>
        <div className="relative h-16 w-px overflow-hidden bg-primary/20">
          <div className="absolute inset-x-0 top-0 h-1/2 w-px bg-primary animate-[scrollLine_2.5s_ease-in-out_infinite]" />
        </div>
      </div>

      <div className="absolute bottom-6 left-6 z-20 md:hidden">
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-primary">
          Status: Active Protection
        </span>
      </div>

      <style>{`
        @keyframes slowZoom {
          0% { transform: scale(1); }
          100% { transform: scale(1.12); }
        }
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}